import { useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useOrderStatus } from '../hooks/useOrderStatus'
import { useBranding } from '../hooks/useBranding'
import { NotFoundError, retryCheckout } from '../api/client'
import { formatZARCents } from '../lib/format'
import type { RestaurantMenu } from '../api/types'
import NotFoundPage from './NotFoundPage'

const TIMEOUT_MS = 60_000

function checkingCopy(hint: string | null): string {
  if (hint === 'success') return 'Confirming your payment…'
  if (hint === 'cancel' || hint === 'failure') return 'Checking your payment status…'
  return 'Checking your order…'
}

const FAILURE_MESSAGES: Record<'payment_failed' | 'cancelled' | 'expired', string> = {
  payment_failed: "Your payment didn't go through. No charge was made — you can try again.",
  cancelled: 'You cancelled checkout before payment completed. Your order was not placed.',
  expired: 'This checkout session expired before payment completed. Your order was not placed.',
}

export default function OrderStatusPage() {
  const { slug, orderId } = useParams<{ slug: string; orderId: string }>()
  const [searchParams] = useSearchParams()
  const hint = searchParams.get('r')
  const queryClient = useQueryClient()
  const { data, error, refetch } = useOrderStatus(orderId ?? '')
  const [hasTimedOut, setHasTimedOut] = useState(false)
  const [retryError, setRetryError] = useState<string | null>(null)
  const [isRetrying, setIsRetrying] = useState(false)
  const retryInFlightRef = useRef(false)

  const menu = queryClient.getQueryData<RestaurantMenu>(['menu', slug])
  useBranding(menu)

  useEffect(() => {
    const timer = setTimeout(() => setHasTimedOut(true), TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [])

  async function handleRetry() {
    // Ref-based guard, same pattern as useCheckout — a state-only guard could
    // let two rapid synchronous calls both slip through before either
    // setState commits.
    if (!orderId || retryInFlightRef.current) return
    retryInFlightRef.current = true
    setIsRetrying(true)
    setRetryError(null)
    try {
      const res = await retryCheckout(orderId)
      window.location.href = res.redirect_url
    } catch {
      setRetryError('Could not restart checkout. Please try again.')
      retryInFlightRef.current = false
      setIsRetrying(false)
    }
  }

  if (error instanceof NotFoundError) {
    return (
      <NotFoundPage
        heading="Order not found"
        message="We couldn't find an order with this link. It may have expired or the link may be incorrect."
      />
    )
  }

  if (error) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center gap-3 px-6 text-center bg-white">
        <p className="text-sm text-neutral-600">
          We couldn't load your order status. Please check your connection.
        </p>
        <button
          onClick={() => void refetch()}
          className="mt-2 px-4 py-2 rounded-lg bg-neutral-900 text-white text-sm font-medium hover:bg-neutral-700 transition-colors"
        >
          Retry
        </button>
      </main>
    )
  }

  if (!data) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center gap-3 px-6 text-center bg-white">
        <p className="text-sm text-neutral-500">Loading your order…</p>
      </main>
    )
  }

  if (data.status === 'pending_payment' && !hasTimedOut) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center gap-3 px-6 text-center bg-white">
        <div
          className="h-8 w-8 rounded-full border-2 border-neutral-300 border-t-neutral-800 animate-spin"
          aria-hidden="true"
        />
        <p className="text-sm text-neutral-600">{checkingCopy(hint)}</p>
      </main>
    )
  }

  if (
    data.status === 'paid' ||
    data.status === 'accepted' ||
    data.status === 'ready' ||
    data.status === 'completed'
  ) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center gap-3 px-6 text-center bg-white">
        <p className="text-5xl" aria-hidden="true">
          🎉
        </p>
        <h1 className="text-xl font-bold text-neutral-800">Thanks for your order!</h1>
        <p className="text-3xl font-bold tracking-wide">{data.short_code}</p>
        <p className="text-sm text-neutral-500">Show this code to staff if you need help.</p>
        {data.table_label && <p className="text-sm text-neutral-600">Table {data.table_label}</p>}
        <ul className="text-sm text-neutral-700">
          {data.items.map((line) => (
            <li key={line.name}>
              {line.quantity}× {line.name} — {formatZARCents(line.line_total_cents)}
            </li>
          ))}
        </ul>
        <p className="text-sm text-neutral-600">Total paid: {formatZARCents(data.total_cents)}</p>
      </main>
    )
  }

  if (
    data.status === 'payment_failed' ||
    data.status === 'cancelled' ||
    data.status === 'expired'
  ) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center gap-3 px-6 text-center bg-white">
        <p className="text-5xl" aria-hidden="true">
          ⚠️
        </p>
        <h1 className="text-xl font-bold text-neutral-800">Order not placed</h1>
        <p className="text-sm text-neutral-500">{FAILURE_MESSAGES[data.status]}</p>
        {retryError && (
          <p role="alert" className="text-xs text-red-600">
            {retryError}
          </p>
        )}
        <button
          onClick={() => void handleRetry()}
          disabled={isRetrying}
          className="mt-2 px-4 py-2 rounded-lg bg-neutral-900 text-white text-sm font-medium hover:bg-neutral-700 transition-colors disabled:opacity-40"
        >
          Try again
        </button>
      </main>
    )
  }

  // data.status === 'pending_payment' && hasTimedOut
  return (
    <main className="min-h-screen flex flex-col items-center justify-center gap-3 px-6 text-center bg-white">
      <p className="text-3xl font-bold tracking-wide">{data.short_code}</p>
      <p className="text-sm text-neutral-600">
        This is taking longer than expected — we&apos;re still checking, no need to refresh.
      </p>
    </main>
  )
}
